<template>
  <div class="sign-page">
    <v-app-bar density="comfortable" color="teal-lighten-4" elevation="1">
      <v-btn icon="mdi-arrow-left" variant="text" @click="clickOnLeft"></v-btn>
      <v-app-bar-title class="text-subtitle-1 font-weight-bold">学生打卡</v-app-bar-title>
    </v-app-bar>

    <v-container class="pa-3">
      <!-- 学生信息展示卡 -->
      <v-card variant="flat" border class="pa-4 mb-3 rounded-xl bg-white text-center">
        <h2 class="text-h6 font-weight-bold text-grey-darken-3 mb-1">{{ studentName }}</h2>
        <div class="d-flex justify-center align-center">
          <v-chip size="small" color="teal" variant="tonal" class="font-weight-medium">
            <v-icon start icon="mdi-check-circle" size="small" />
            今日已打卡 {{ kick_count || 0 }} 次
          </v-chip>
        </div>
      </v-card>

      <!-- 打卡类型标题栏与展开收起切换 -->
      <div class="section-header-row mb-2 px-1">
        <div class="header-left">
          <v-icon color="teal" class="header-icon mr-1" size="small">mdi-checkbox-marked-circle-outline</v-icon>
          <span class="header-title">打卡类型</span>
          <span class="header-hint ml-1">
            {{ statusProgress > 0.5 ? '(右滑收起)' : '(左滑展开)' }}
          </span>
        </div>
        <div class="header-right">
          <v-btn
            variant="text"
            size="x-small"
            density="compact"
            color="teal-darken-1"
            class="header-btn"
            @click="toggleStatusExpand"
          >
            <span class="btn-text mr-0.5">{{ statusProgress > 0.5 ? '收起' : '更多' }}</span>
            <v-icon
              icon="mdi-chevron-down"
              size="14"
              class="chevron-icon"
              :style="{
                transform: `rotate(${statusProgress * 180}deg)`,
                transition: isStatusTouching ? 'none' : 'transform 0.28s ease'
              }"
            />
          </v-btn>
        </div>
      </div>

      <!-- 打卡胶囊：手势跟手展开/收回，第二排渐变展示与回缩 -->
      <div
        class="pill-slider-wrapper mb-4"
        :class="{ 'is-expanded': statusProgress > 0.5 }"
        :style="{
          height: statusContainerHeight,
          transition: isStatusTouching ? 'none' : 'height 0.28s cubic-bezier(0.25, 1, 0.5, 1), background-color 0.25s ease'
        }"
        @touchstart.passive="onStatusTouchStart"
        @touchmove="onStatusTouchMove"
        @touchend="onStatusTouchEnd"
        @touchcancel="onStatusTouchEnd"
      >
        <div class="pill-slider-grid px-1 py-1">
          <v-btn
            v-for="(item, index) in statusList"
            :key="item.id"
            rounded="pill"
            height="38"
            :color="item.color"
            variant="tonal"
            :loading="loading && currentAction === item.id"
            :disabled="hiding"
            class="pill-grid-btn font-weight-bold"
            :style="getStatusItemStyle(index)"
            @click="signIn(item.id)"
          >
            <v-icon
              start
              :icon="item.icon"
              size="14"
              class="mr-1"
            />
            <span class="pill-btn-text">{{ item.name }}</span>
          </v-btn>
        </div>
      </div>

      <!-- 打卡记录时间轴 -->
      <v-card variant="flat" border class="pa-3 rounded-xl bg-white">
        <div class="d-flex align-center mb-2 px-2 text-subtitle-2 text-grey-darken-2 font-weight-bold">
          <v-icon icon="mdi-history" size="small" class="mr-1" color="primary" />
          今日流水记录
        </div>

        <div v-if="signData.length === 0" class="text-center py-6 text-grey">
          <v-icon icon="mdi-clipboard-text-outline" size="large" class="mb-1" />
          <div class="text-caption">今日暂无打卡记录</div>
        </div>

        <v-timeline v-else side="end" density="compact" class="px-2 sign-timeline">
          <v-timeline-item
            v-for="n in signData"
            :key="n.id || n.created_at"
            :dot-color="colorFilter(n.status)"
            size="x-small"
          >
            <div class="d-flex align-center justify-space-between w-100 py-1">
              <div class="d-flex align-center mr-2 text-truncate" style="min-width: 0;">
                <span class="text-body-2 font-weight-bold mr-2 flex-shrink-0">{{ formatTime(n.created_at) }}</span>
                <span class="text-caption text-grey-darken-1 font-weight-medium text-truncate">{{ n.teacher_name }}</span>
              </div>
              <v-chip size="small" :color="colorFilter(n.status)" variant="tonal" class="flex-shrink-0 font-weight-medium">
                {{ statusText(n.status) }}
              </v-chip>
            </div>
          </v-timeline-item>
        </v-timeline>
      </v-card>
    </v-container>

    <!-- 反馈提示条 -->
    <v-snackbar v-model="snackbar" location="bottom" :color="msgColor" :timeout="timeout">
      {{ text }}
      <template v-slot:actions>
        <v-btn variant="text" color="white" @click="revoke">
          <v-icon start icon="mdi-undo-variant" size="small" /> 撤销
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { getRecord, createRecord, deleteRecord } from "@/api";
import { statusFilter, statusTransfer } from "@/utils";
import moment from "moment";

export default {
  name: "Sign",
  data() {
    return {
      snackbar: false,
      text: "",
      timeout: 8000,
      msgColor: "",
      loading: false,
      currentAction: null,
      hiding: false,
      studentName: this.$route.query.nick_name || "",
      id: parseInt(this.$route.query.id),
      signData: [],
      kick_count: 0,
      user_id: parseInt(localStorage.getItem("id")),
      statusList: [
        { id: 1, name: "个训", color: "success", icon: "mdi-account" },
        { id: 2, name: "集体", color: "teal", icon: "mdi-account-multiple" },
        { id: 7, name: "督导", color: "deep-purple", icon: "mdi-account-tie" },
        { id: 3, name: "影子", color: "indigo", icon: "mdi-bee" },
        { id: 4, name: "辅课", color: "amber-darken-2", icon: "mdi-account-heart" },
        { id: 0, name: "缺席", color: "error", icon: "mdi-close-circle-outline" },
        { id: 5, name: "20MIN", color: "blue-grey", icon: "mdi-clock-outline" },
        { id: 6, name: "30MIN", color: "brown", icon: "mdi-clock-time-four-outline" },
      ],
      statusProgress: 0,
      isStatusTouching: false,
      statusTouchStartX: 0,
      statusTouchStartY: 0,
      statusStartProgress: 0,
      isStatusHorizontalDrag: null,
    };
  },
  computed: {
    statusContainerHeight() {
      const minH = 46;
      const maxH = 92;
      return `${minH + (maxH - minH) * this.statusProgress}px`;
    },
  },
  mounted() {
    this.getRecordByStudentId();
  },
  methods: {
    getStatusItemStyle(index) {
      if (index < 4) return {};
      const p = this.statusProgress;
      return {
        opacity: p,
        transform: `translateY(${(1 - p) * 14}px)`,
        pointerEvents: p > 0.6 ? "auto" : "none",
        transition: this.isStatusTouching
          ? "none"
          : "opacity 0.28s cubic-bezier(0.25, 1, 0.5, 1), transform 0.28s cubic-bezier(0.25, 1, 0.5, 1)",
      };
    },
    toggleStatusExpand() {
      this.isStatusTouching = false;
      this.statusProgress = this.statusProgress > 0.5 ? 0 : 1;
    },
    onStatusTouchStart(e) {
      if (!e.touches || !e.touches[0]) return;
      this.statusTouchStartX = e.touches[0].clientX;
      this.statusTouchStartY = e.touches[0].clientY;
      this.statusStartProgress = this.statusProgress;
      this.isStatusTouching = true;
      this.isStatusHorizontalDrag = null;
    },
    onStatusTouchMove(e) {
      if (!this.isStatusTouching || !e.touches || !e.touches[0]) return;
      const deltaX = e.touches[0].clientX - this.statusTouchStartX;
      const deltaY = e.touches[0].clientY - this.statusTouchStartY;
      if (this.isStatusHorizontalDrag === null) {
        if (Math.abs(deltaX) > 6 && Math.abs(deltaX) > Math.abs(deltaY) * 1.1) {
          this.isStatusHorizontalDrag = true;
        } else if (Math.abs(deltaY) > 6) {
          this.isStatusHorizontalDrag = false;
        }
      }
      if (this.isStatusHorizontalDrag) {
        if (e.cancelable) e.preventDefault();
        const dragDistance = 85;
        const nextProgress = this.statusStartProgress - deltaX / dragDistance;
        this.statusProgress = Math.max(0, Math.min(1, nextProgress));
      }
    },
    onStatusTouchEnd() {
      if (!this.isStatusTouching) return;
      this.isStatusTouching = false;
      this.statusProgress = this.statusProgress >= 0.4 ? 1 : 0;
      this.isStatusHorizontalDrag = null;
    },
    colorFilter(status) {
      return statusFilter(status);
    },
    statusText(status) {
      return statusTransfer(status) || "打卡";
    },
    formatTime(utcTime) {
      return moment(utcTime * 1000).format("HH:mm");
    },
    getRecordByStudentId() {
      const startDate = moment().format("YYYY-MM-DD 00:00:00");
      const end_at = moment().endOf("day").format("YYYY-MM-DD HH:mm:ss.SSS");
      getRecord({
        student_id: parseInt(this.$route.query.id),
        start_at: startDate,
        end_at: end_at,
      }).then((res) => {
        this.signData = res.data.data.records || [];
        this.kick_count = this.signData.length;
      });
    },
    revoke() {
      if (!this.signData || this.signData.length === 0) return;
      deleteRecord({ id: this.signData.slice(-1)[0].id }).then((res) => {
        this.snackbar = false;
        if (res.data.code == "200" || res.data.code == 200) {
          this.getRecordByStudentId();
          this.hiding = false;
        }
      });
    },
    signIn(action) {
      this.loading = true;
      this.currentAction = action;
      createRecord({
        status: action,
        student_id: this.id,
        user_id: this.user_id,
      }).then((res) => {
        if (res.data.code == "200" || res.data.code == 200) {
          // 打卡成功不弹 toast，界面直接刷新流水
        } else {
          this.snackbar = true;
          this.text = res.data.msg || "打卡失败";
          this.msgColor = "error";
        }
        this.loading = false;
        this.currentAction = null;
        this.getRecordByStudentId();
      }).catch(() => {
        this.loading = false;
        this.currentAction = null;
      });
    },
    clickOnLeft() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.sign-page {
  min-height: 100vh;
  background-color: #f8fafc;
  padding-bottom: 24px;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
  overflow-x: hidden;
}

/* 统一标题行与像素级垂直居中对齐 */
.section-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 26px;
  line-height: 1;
}

.header-left,
.header-right {
  display: flex;
  align-items: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.header-title {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.header-hint {
  font-size: 11px;
  color: #94a3b8;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  user-select: none;
}

.header-btn {
  font-weight: 700 !important;
  height: 22px !important;
  min-height: 22px !important;
  padding: 0 2px !important;
  display: inline-flex !important;
  align-items: center !important;
  line-height: 1 !important;
}

.header-btn :deep(.v-btn__content) {
  display: inline-flex;
  align-items: center;
  line-height: 1;
  height: 100%;
}

.btn-text {
  font-size: 12px;
  line-height: 1;
  display: inline-block;
}

.chevron-icon {
  display: inline-flex;
  align-items: center;
}

.pill-slider-wrapper {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  touch-action: pan-y;
}

.pill-slider-wrapper.is-expanded {
  border-color: rgba(0, 137, 123, 0.2);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.04);
}

.pill-slider-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px 6px;
  width: 100%;
}

.pill-grid-btn {
  width: 100% !important;
  min-width: 0 !important;
  padding: 0 4px !important;
  margin: 0 !important;
  font-size: 12px !important;
  letter-spacing: 0 !important;
  box-shadow: none !important;
  will-change: transform, opacity;
}

.pill-grid-btn :deep(.v-btn__content) {
  width: 100%;
  justify-content: center;
}

.pill-btn-text {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sign-timeline :deep(.v-timeline-item__body) {
  width: 100% !important;
  justify-self: stretch !important;
  min-width: 0 !important;
}
</style>
